/**
 * Formats a past date relative to now using `Intl.RelativeTimeFormat`.
 *
 * Picks the largest unit that produces a value >= 1 (second → minute → hour → day → week → month → year).
 *
 * @param date - Date or ISO string to compare against now
 * @param locale - BCP-47 locale (e.g. 'en', 'fr')
 * @returns Localized relative string, e.g. "il y a 3 jours" or "3 days ago"
 *
 * @example
 * formatRelativeIntl('2025-01-01', 'fr') // 'il y a 4 mois'
 * formatRelativeIntl(new Date(Date.now() - 5 * 60_000), 'en') // '5 minutes ago'
 */
export function formatRelativeIntl(date: string | Date, locale: string = 'en'): string {
  const target = typeof date === 'string' ? new Date(date) : date
  const fmt = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const diffSec = Math.round((Date.now() - target.getTime()) / 1000)

  if (diffSec < 60) return fmt.format(-diffSec, 'second')
  if (diffSec < 3600) return fmt.format(-Math.round(diffSec / 60), 'minute')
  if (diffSec < 86400) return fmt.format(-Math.round(diffSec / 3600), 'hour')
  if (diffSec < 86400 * 7) return fmt.format(-Math.round(diffSec / 86400), 'day')
  if (diffSec < 86400 * 30) return fmt.format(-Math.round(diffSec / (86400 * 7)), 'week')
  if (diffSec < 86400 * 365) return fmt.format(-Math.round(diffSec / (86400 * 30)), 'month')
  return fmt.format(-Math.round(diffSec / (86400 * 365)), 'year')
}
